/* eslint-disable camelcase */
import { sortBy } from 'lodash';
import { BASE, Params, defaults } from './__fixtures__/service-fixtures';
import { baseApi, parameterize } from './base.service';
import keysToCamelCase from '../utils/keys-to-camel-case';
import Membership from '../models/membership';

const formMembershipDefaults = defaults;

type FormConditionals = {
  transform: boolean;
  permissions: Record<any, any> | undefined;
  object_id: string;
  with_issues_and_tasks_only?: boolean;
};
interface MembershipFilters extends Params { role?: string }

type GetMembershipsResponse = {
  memberships: Membership[];
  currentPage: number;
  perPage: number;
  totalCount: number;
  totalPages: number;
};

export interface MembershipsResponse extends Response {
  data: {
    memberships: Record<any, any>;
  };
}

const transformMembersResponse = (response: any, _meta, arg: FormConditionals) => {
  let res = response; // m1g :: in usage for the AssignmentField
  const { transform, permissions } = arg;
  const noAccess = ({ user_id }) => !permissions?.projectAccessibleUserIds?.includes(user_id);
  if (transform) {
    res = response.memberships.map((user) => {
      if (permissions && noAccess(user)) {
        return {
          value: user.user_id,
          label: `${user.fullName || user.user} (${user.email})`,
          disabled: true,
          tooltipText: 'Add assignee to the current project to enable',
        };
      }
      return {
        value: user.user_id,
        label: `${user.fullName || user.user} (${user.email})`,
      };
    });
  }
  return transform ? sortBy(res, 'label') : res; // use server side sort;
};

export const membershipsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getMemberships: build.query<GetMembershipsResponse, Partial<MembershipFilters>>({
      query(options) {
        const params = new URLSearchParams();
        params.append('per_page', options.per_page ? options.per_page.toString() : '1000');
        params.append('page', options.page ? options.page.toString() : '1');
        params.append('sort_direction', options.sort_direction ?? 'ASC');
        params.append('sort', options.sort ?? 'user');

        if (options.search && options.search.length >= 2) {
          params.append('search', options.search);
        }
        if (options.role) {
          params.append('role', options.role);
        }
        return `${BASE}memberships.json?${params.toString()}`;
      },
      transformResponse(response: any) {
        return keysToCamelCase(response);
      },
    }),
    getFormMemberships: build.query<Record<any, any>, {}>({
      query: ({
        // removes transform and permissions from the params...
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        transform, permissions, search, object_id, ...options
      }: Partial<Params> & FormConditionals) => {
        const params = { ...formMembershipDefaults, ...options };

        if (search && search.length >= 2) {
          params.search = search;
        }
        return `${BASE}forms/${object_id}/members.json?${parameterize(params)}`;
      },
      transformResponse: transformMembersResponse,
    }),
    getITAMemberships: build.query<Record<any, any>, {}>({
      query: ({
        // removes transform and permissions from the params...
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        transform, permissions, search, ...options
      }: Partial<Params> & FormConditionals) => {
        const params = { ...formMembershipDefaults, ...options };
        params.with_issues_and_tasks_only = true;

        if (search && search.length >= 2) {
          params.search = search;
        }
        return `${BASE}/memberships.json?${parameterize(params)}`;
      },
      transformResponse: transformMembersResponse,
    }),
  }),
});

export const { useGetMembershipsQuery, useGetFormMembershipsQuery, useGetITAMembershipsQuery } = membershipsApi;
